import { useCallback, useEffect, useState, type RefObject } from "react";
import { authCalls, readAuthFailure, waitWords, type Session } from "./authApi";
import { useGoogleToken } from "./useGoogleToken";

export type SignInNotice =
  | { kind: "mismatch" }
  | { kind: "offline" }
  | { kind: "rate"; wait: string }
  | { kind: "google-none"; email: string }
  | { kind: "google-failed" }
  | { kind: "locked"; email: string };

/**
 * Password and Google sign-in, as the Sign in page, the Join page's "Sign in
 * to accept" and the session dialog all need it. One message for every miss
 * (M01): focus goes to the password with its text selected, ready to retype.
 */
export function useSignIn({
  onSignedIn,
  onNotVerified,
  passwordRef,
}: {
  onSignedIn: (session: Session) => void | Promise<void>;
  /** The password field, focused with its text selected after a miss. */
  passwordRef?: RefObject<HTMLInputElement | null>;
  /** A correct password on an unverified address. */
  onNotVerified?: (email: string) => void;
}) {
  const [pending, setPending] = useState<"password" | "google" | null>(null);
  const [notice, setNotice] = useState<SignInNotice | null>(null);
  const [waitUntil, setWaitUntil] = useState<number | null>(null);

  useEffect(() => {
    if (!waitUntil) return;
    const id = window.setTimeout(() => setWaitUntil(null), Math.max(0, waitUntil - Date.now()));
    return () => window.clearTimeout(id);
  }, [waitUntil]);

  const fail = useCallback(
    (error: unknown, via: "password" | "google") => {
      const failure = readAuthFailure(error);
      if (failure.kind === "offline") return setNotice({ kind: "offline" });
      if (failure.kind === "rate") {
        setWaitUntil(Date.now() + failure.waitSeconds * 1000);
        return setNotice({ kind: "rate", wait: waitWords(failure.waitSeconds) });
      }
      // Locked from the CMS: only a password reset by email lets them back in.
      if (failure.code === "ACCOUNT_LOCKED") {
        return setNotice({ kind: "locked", email: String(failure.data.email ?? "") });
      }
      if (failure.code === "EMAIL_NOT_VERIFIED" && onNotVerified) {
        return onNotVerified(String(failure.data.email ?? ""));
      }
      if (via === "google") {
        if (failure.code === "SIGNUP_NOT_ALLOWED" && typeof failure.data.email === "string") {
          return setNotice({ kind: "google-none", email: failure.data.email });
        }
        return setNotice({ kind: "google-failed" });
      }
      setNotice({ kind: "mismatch" });
      window.requestAnimationFrame(() => {
        passwordRef?.current?.focus();
        passwordRef?.current?.select();
      });
    },
    [onNotVerified, passwordRef],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      setPending("password");
      try {
        const session = await authCalls.login(email.trim(), password);
        setNotice(null);
        await onSignedIn(session);
      } catch (error) {
        fail(error, "password");
      } finally {
        setPending(null);
      }
    },
    [fail, onSignedIn],
  );

  const google = useGoogleToken({
    onToken: async (accessToken) => {
      setPending("google");
      try {
        const session = await authCalls.google(accessToken);
        setNotice(null);
        await onSignedIn(session);
      } catch (error) {
        fail(error, "google");
      } finally {
        setPending(null);
      }
    },
    onFailure: () => setNotice({ kind: "google-failed" }),
  });

  return {
    pending,
    notice,
    setNotice,
    waiting: waitUntil !== null,
    signIn,
    google: () => google(),
  };
}
